import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Copy, Check, Tag, Power, PowerOff, TrendingUp } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import AdminPageHeader from '../../components/AdminPageHeader';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';
import { formatEUR } from '../../utils/format';

const emptyForm = {
  code: '',
  discount_type: 'percentage',
  discount_value: 10,
  min_order_value: 0,
  max_uses: 100,
  valid_until: '',
  description: '',
};

const DiscountCodes = () => {
  const [codes, setCodes] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, used: 0, total_redemptions: 0 });
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  const fetchCodes = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/coupons/admin/all', { params: { global_only: true } });
      setCodes(res.data?.coupons || []);
      setStats(res.data?.statistics || {});
    } catch (e) {
      toast.error('Fehler beim Laden der Rabattcodes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCodes(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.code.trim()) return toast.error('Code darf nicht leer sein');
    const payload = {
      code: formData.code.trim().toUpperCase(),
      discount_type: formData.discount_type,
      discount_value: Number(formData.discount_value) || 0,
      min_order_value: Number(formData.min_order_value) || 0,
      max_uses: Number(formData.max_uses) || 1,
      valid_until: formData.valid_until
        ? new Date(formData.valid_until + 'T23:59:59').toISOString()
        : null,
      description: formData.description || null,
    };
    try {
      await axiosInstance.post('/coupons/admin/create', payload);
      toast.success('Rabattcode erstellt');
      setShowAddDialog(false);
      setFormData(emptyForm);
      fetchCodes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Erstellen');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Rabattcode wirklich löschen?')) return;
    try {
      await axiosInstance.delete(`/coupons/admin/${id}`);
      toast.success('Gelöscht');
      fetchCodes();
    } catch { toast.error('Fehler beim Löschen'); }
  };

  const handleToggle = async (id) => {
    try {
      await axiosInstance.patch(`/coupons/admin/${id}/toggle-active`);
      fetchCodes();
    } catch { toast.error('Fehler beim Umschalten'); }
  };

  const handleCopy = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success('Code kopiert');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const formatDate = (iso) => iso
    ? new Date(iso).toLocaleDateString('de-DE', { year: 'numeric', month: 'short', day: '2-digit' })
    : '–';

  const isExpired = (iso) => iso && new Date(iso) < new Date();

  const statusOf = (c) => {
    if (!c.is_active) return { label: 'Deaktiviert', color: 'bg-gray-100 text-gray-500' };
    if (isExpired(c.valid_until)) return { label: 'Abgelaufen', color: 'bg-orange-50 text-orange-700' };
    const maxUses = c.max_uses || 0;
    const uses = c.uses_count || 0;
    if (maxUses > 0 && uses >= maxUses) return { label: 'Aufgebraucht', color: 'bg-red-50 text-red-700' };
    return { label: 'Aktiv', color: 'bg-emerald-50 text-emerald-700' };
  };

  if (loading) return <div className="flex justify-center items-center min-h-screen"><p>Lädt...</p></div>;

  return (
    <div className="container mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 lg:py-8" style={{ backgroundColor: '#F5EDE4', minHeight: '100vh' }} data-testid="discount-codes-page">
      <AdminPageHeader
        title="Rabattcodes"
        subtitle={`${codes.length} Code${codes.length !== 1 ? 's' : ''} · ${stats.total_redemptions || 0} Einlösungen gesamt`}
        icon={Tag}
      />

      {/* Statistik-Karten */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <div className="bg-white border border-[#E8DCC8] p-3">
          <p className="text-[10px] uppercase tracking-widest text-[#8B7355]">Aktive Codes</p>
          <p className="text-2xl font-bold text-[#5D4E3C]" data-testid="stat-active">{stats.active || 0}</p>
        </div>
        <div className="bg-white border border-[#E8DCC8] p-3">
          <p className="text-[10px] uppercase tracking-widest text-[#8B7355]">Insgesamt</p>
          <p className="text-2xl font-bold text-[#5D4E3C]">{stats.total || 0}</p>
        </div>
        <div className="bg-white border border-[#E8DCC8] p-3">
          <p className="text-[10px] uppercase tracking-widest text-[#8B7355]">Einlösungen</p>
          <p className="text-2xl font-bold text-[#5D4E3C] flex items-center gap-1">
            <TrendingUp className="h-4 w-4 text-emerald-600" /> {stats.total_redemptions || 0}
          </p>
        </div>
        <div className="bg-white border border-[#E8DCC8] p-3">
          <p className="text-[10px] uppercase tracking-widest text-[#8B7355]">Einlöse-Rate</p>
          <p className="text-2xl font-bold text-[#5D4E3C]">{stats.redemption_rate || 0}%</p>
        </div>
      </div>

      {/* Aktionen */}
      <div className="mb-3 p-2 px-3 flex items-center justify-between" style={{ backgroundColor: '#C4A084' }}>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded flex items-center justify-center bg-[#F0FFF0]">
            <Plus className="h-4 w-4 text-[#60B060]" />
          </div>
          <h2 className="text-base font-semibold text-white">Aktionen</h2>
        </div>
        <Button
          onClick={() => setShowAddDialog(true)}
          className="bg-white text-[#C4A084] hover:bg-white/90 rounded-none"
          data-testid="new-code-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Neuer Code
        </Button>
      </div>

      {/* Codes-Liste */}
      <div className="mb-3 p-2 px-3 flex items-center gap-2 mt-4" style={{ backgroundColor: '#C4A084' }}>
        <div className="w-6 h-6 rounded flex items-center justify-center bg-[#F8F0FF]">
          <Tag className="h-4 w-4 text-[#A070D0]" />
        </div>
        <h2 className="text-base font-semibold text-white">Alle Rabattcodes</h2>
      </div>

      <Card className="rounded-none border-[#C4A084]">
        <CardContent className="p-0">
          <div className="admin-table-container overflow-x-auto">
            <table className="w-full admin-table text-sm">
              <thead>
                <tr className="border-b" style={{ backgroundColor: '#F5EDE4' }}>
                  <th className="p-3 text-left font-semibold text-[#5D4E3C]">Code</th>
                  <th className="p-3 text-left font-semibold text-[#5D4E3C]">Rabatt</th>
                  <th className="p-3 text-left font-semibold text-[#5D4E3C]">Mindestwert</th>
                  <th className="p-3 text-left font-semibold text-[#5D4E3C]">Einlösungen</th>
                  <th className="p-3 text-left font-semibold text-[#5D4E3C]">Gültig bis</th>
                  <th className="p-3 text-left font-semibold text-[#5D4E3C]">Status</th>
                  <th className="p-3 text-right font-semibold text-[#5D4E3C]">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {codes.length === 0 ? (
                  <tr><td colSpan="7" className="p-8 text-center text-[#8B7355]">Keine Rabattcodes vorhanden</td></tr>
                ) : (
                  codes.map((c) => {
                    const st = statusOf(c);
                    const uses = c.uses_count || 0;
                    const maxUses = c.max_uses || 0;
                    return (
                      <tr key={c.id} className="border-b hover:bg-[#FAF8F5]" data-testid={`code-row-${c.id}`}>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <code className="bg-[#F8F0FF] text-[#A070D0] px-2 py-1 font-mono font-bold text-xs">
                              {c.code}
                            </code>
                            <button onClick={() => handleCopy(c.code)} className="text-[#8B7355] hover:text-[#5D4E3C]">
                              {copiedCode === c.code ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                            </button>
                          </div>
                          {c.description && (
                            <p className="text-[11px] text-[#8B7355] mt-1">{c.description}</p>
                          )}
                        </td>
                        <td className="p-3">
                          <span className="bg-emerald-50/70 text-emerald-700 px-2 py-0.5 text-xs font-semibold">
                            {c.discount_type === 'fixed' ? `-${formatEUR(Number(c.discount_value))}` : `-${c.discount_value}%`}
                          </span>
                        </td>
                        <td className="p-3 text-xs text-gray-600">
                          {c.min_order_value ? `ab ${formatEUR(Number(c.min_order_value))}` : '–'}
                        </td>
                        <td className="p-3 text-xs">
                          <span className="text-gray-700 font-medium">{uses}</span>
                          {maxUses > 0 && <span className="text-gray-400"> / {maxUses}</span>}
                        </td>
                        <td className="p-3 text-xs text-gray-600">{formatDate(c.valid_until)}</td>
                        <td className="p-3">
                          <span className={`${st.color} px-2 py-0.5 text-xs`}>{st.label}</span>
                        </td>
                        <td className="p-3 text-right">
                          <Button
                            onClick={() => handleToggle(c.id)}
                            variant="ghost"
                            size="sm"
                            className={c.is_active ? 'text-orange-600 hover:bg-orange-50' : 'text-emerald-600 hover:bg-emerald-50'}
                            title={c.is_active ? 'Deaktivieren' : 'Aktivieren'}
                            data-testid={`code-toggle-${c.id}`}
                          >
                            {c.is_active ? <PowerOff className="w-3.5 h-3.5" /> : <Power className="w-3.5 h-3.5" />}
                          </Button>
                          <Button
                            onClick={() => handleDelete(c.id)}
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:bg-red-50"
                            title="Löschen"
                            data-testid={`code-delete-${c.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Add-Dialog */}
      {showAddDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-none max-w-lg w-full mx-4">
            <div className="p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-bold mb-4 text-[#5D4E3C]" style={{ fontFamily: 'Georgia, serif' }}>
                Neuer Rabattcode
              </h2>
              <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                  <Label htmlFor="code" className="text-sm font-medium">Code *</Label>
                  <Input
                    id="code"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="z. B. SOMMER25"
                    required
                    className="mt-1 rounded-none"
                    data-testid="form-code"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm font-medium">Rabatt-Typ *</Label>
                    <select
                      value={formData.discount_type}
                      onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                      className="mt-1 w-full border border-input px-3 py-2 text-sm bg-white focus:border-[#C4A084] outline-none"
                      data-testid="form-type"
                    >
                      <option value="percentage">Prozent (%)</option>
                      <option value="fixed">Fester Betrag (€)</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">
                      Wert * {formData.discount_type === 'percentage' ? '(%)' : '(€)'}
                    </Label>
                    <Input
                      type="number"
                      step={formData.discount_type === 'percentage' ? '1' : '0.01'}
                      min="0.01"
                      max={formData.discount_type === 'percentage' ? '100' : '9999'}
                      value={formData.discount_value}
                      onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                      required
                      className="mt-1 rounded-none"
                      data-testid="form-value"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm font-medium">Mindestbestellwert (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.min_order_value}
                      onChange={(e) => setFormData({ ...formData, min_order_value: e.target.value })}
                      className="mt-1 rounded-none"
                      placeholder="0 = kein Minimum"
                      data-testid="form-min-order"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Max. Einlösungen</Label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.max_uses}
                      onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                      className="mt-1 rounded-none"
                      placeholder="z. B. 100"
                      data-testid="form-max-uses"
                    />
                    <p className="text-[10px] text-[#8B7355] mt-0.5">wie oft insgesamt einlösbar</p>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Gültig bis (Datum)</Label>
                  <Input
                    type="date"
                    value={formData.valid_until}
                    onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                    className="mt-1 rounded-none"
                    placeholder="Leer = unbegrenzt"
                    data-testid="form-valid-until"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Beschreibung</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="mt-1 rounded-none"
                    placeholder="z. B. Sommer-Aktion 2026"
                    data-testid="form-description"
                  />
                </div>
                <div className="flex flex-col-reverse sm:flex-row gap-2 pt-3">
                  <Button
                    type="button"
                    onClick={() => { setShowAddDialog(false); setFormData(emptyForm); }}
                    className="rounded-none flex-1 sm:flex-none bg-[#FFEBEE] text-[#E57373] hover:bg-[#FFCDD2] border-0"
                  >
                    Abbrechen
                  </Button>
                  <Button
                    type="submit"
                    className="rounded-none flex-1 sm:flex-none bg-[#E8F5E9] text-[#4CAF50] hover:bg-[#C8E6C9] border-0"
                    data-testid="form-submit"
                  >
                    Code erstellen
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiscountCodes;
